import { Link, useNavigate } from "react-router-dom";
import { Droplets, Menu, X, User, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-white/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <div className="w-9 h-9 rounded-xl bg-[#064E3B] flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-base font-semibold text-stone-900">IV Therapy Finder</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Find. Compare. Book.</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-stone-700">
          <Link to="/search" className="hover:text-[#064E3B] transition" data-testid="nav-find-providers">Find Providers</Link>
          <Link to="/search?type=mobile" className="hover:text-[#064E3B] transition" data-testid="nav-mobile">Mobile IV</Link>
          <Link to="/blog" className="hover:text-[#064E3B] transition" data-testid="nav-blog">Blog</Link>
          <Link to="/for-businesses" className="hover:text-[#064E3B] transition" data-testid="nav-for-business">For Businesses</Link>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#064E3B] to-[#10B981] text-white text-xs font-semibold flex items-center justify-center">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                  {user.name?.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold text-stone-900">{user.name}</div>
                  <div className="text-xs font-normal text-stone-500">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard/user")} data-testid="menu-user-dashboard">
                  <User className="w-4 h-4 mr-2" /> My account
                </DropdownMenuItem>
                {(user.role === "business" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => nav("/dashboard/business")} data-testid="menu-business-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Business dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/add-business")} data-testid="menu-add-business">List a business</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/claim-business")} data-testid="menu-claim-business">Claim a business</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login">Sign in</Button>
              <Button
                onClick={() => nav("/add-business")}
                className="bg-[#064E3B] hover:bg-[#053d2f] text-white rounded-full px-5"
                data-testid="nav-list-business"
              >
                List Your Business
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-lg hover:bg-stone-100"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white px-4 py-4 space-y-2">
          <Link to="/search" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium" data-testid="mobile-nav-search">Find Providers</Link>
          <Link to="/search?type=mobile" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">Mobile IV</Link>
          <Link to="/for-businesses" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">For Businesses</Link>
          {user ? (
            <>
              <Link to="/dashboard/user" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">My account</Link>
              {(user.role === "business" || user.role === "admin") && (
                <Link to="/dashboard/business" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">Business dashboard</Link>
              )}
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">Admin panel</Link>}
              <button onClick={() => { logout(); setOpen(false); }} className="block py-2 text-sm font-medium text-left text-red-600">Sign out</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">Sign in</Link>
          )}
          <Link
            to="/add-business"
            onClick={() => setOpen(false)}
            className="block py-2.5 text-sm font-semibold text-white bg-[#064E3B] rounded-full text-center"
          >
            List Your Business
          </Link>
        </div>
      )}
    </header>
  );
}
